import { useState } from "react";
import { useSearchParams, Link } from "react-router-dom";
import axios from "axios";
import { CheckCircle2, Loader2, KeyRound } from "lucide-react";
import { API, formatApiError } from "@/lib/api";
import { Field, inputProps } from "@/components/Field";
import { PortalShell } from "@/pages/AdminLogin";

const OnboardingActivate = () => {
  const [params] = useSearchParams();
  const token = params.get("token") || "";
  const [form, setForm] = useState({ password: "", confirm: "" });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [done, setDone] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    if (form.password.length < 8) {
      setError("Password must be at least 8 characters.");
      return;
    }
    if (form.password !== form.confirm) {
      setError("Passwords do not match.");
      return;
    }
    setLoading(true);
    try {
      await axios.post(`${API}/onboarding/activate`, { token, password: form.password });
      setDone(true);
    } catch (err) {
      setError(formatApiError(err));
    } finally {
      setLoading(false);
    }
  };

  if (done) {
    return (
      <PortalShell title="You're all set!" subtitle="Your onboarding account is now active.">
        <div className="mt-8 text-center">
          <CheckCircle2 className="mx-auto h-12 w-12 text-gold" strokeWidth={1.5} />
          <p className="mt-4 text-sm text-charcoal-muted">
            Your password has been created. You can now sign in to your secure onboarding portal.
          </p>
          <Link to="/onboarding" data-testid="activate-login-link" className="btn-pink mt-8 w-full">
            Sign In to Onboarding
          </Link>
        </div>
      </PortalShell>
    );
  }

  return (
    <PortalShell title="Activate Your Account" subtitle="Create a password to secure your onboarding portal.">
      {!token ? (
        <p data-testid="activate-invalid" className="mt-8 rounded-xl border border-blush-300 bg-blush-50 p-4 text-center text-sm text-charcoal-muted">
          This activation link is missing its security token. Please open the full link from your invitation email.
        </p>
      ) : (
        <form onSubmit={submit} className="mt-8 grid gap-5" data-testid="onboarding-activate-form">
          <Field label="Create password" htmlFor="oa-password" required>
            <input id="oa-password" type="password" required minLength={8} value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} placeholder="At least 8 characters" {...inputProps("onboarding-activate-password-input")} />
          </Field>
          <Field label="Confirm password" htmlFor="oa-confirm" required>
            <input id="oa-confirm" type="password" required value={form.confirm} onChange={(e) => setForm({ ...form, confirm: e.target.value })} placeholder="Repeat your password" {...inputProps("onboarding-activate-confirm-input")} />
          </Field>
          {error && (
            <p data-testid="activate-error" className="rounded-xl border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">{error}</p>
          )}
          <button type="submit" data-testid="onboarding-activate-submit" disabled={loading} className="btn-pink w-full disabled:opacity-60">
            {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : <KeyRound className="h-4 w-4" />}
            {loading ? "Activating..." : "Activate My Account"}
          </button>
          <p className="text-center text-xs text-charcoal-faint">
            Prefer Google? You can also sign in with the Google account that received your invitation.
          </p>
        </form>
      )}
    </PortalShell>
  );
};

export default OnboardingActivate;
